import { Sidebar, SidebarProvider } from '@Sage/ssm-web5';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { INTERVAL_NEW_MESSAGES } from 'constants/Environment';
import {
    ACTIVITIES,
    AGENDA,
    BILLBOARD,
    CAMPAIGNS,
    COMPANIES,
    CONTACTS,
    CONVERSATIONS,
    DOCUMENTS,
    Entities,
    KPIS,
    METRICS,
    OPPORTUNITIES,
    PRODUCTS,
    SALESORDERS,
} from 'constants/Entities';
import { MessagesActions, ConfigActions } from 'actions';

import AnnounceKitComponent from 'containers/components/AnnounceKitComponent';
import MenuSampleData from 'containers/components/MenuSampleData';
import {
    getActivitiesNotificationsBadge,
    getEnableTimeLine,
} from 'containers/Activities/utils/timeline';
import {
    canAccessCampaignsSection,
    canAccessEmailCampaigns,
    getCampaignsDefaultRoute,
} from 'containers/campaigns/utils/campaignPermissions';
import { getRoute, ensureRoute, hashHistory } from 'utils/routes';
import { logEvent } from 'utils/tracking';
import {
    getSelectedMultiEnvironmentIds,
    isMultiEnvironmentModeActive,
} from 'utils/multiEnvironment';

const EMPTY_ARRAY = [];
const EMPTY_LOGO = { square: null, rectangular: null };
const EMPTY_OBJECT = {};
const PAGE_WIDGET_IMPLEMENTATIONS = [
    '34396', // FERROLI
    '58787', // AXA
    '66050', // AXA DUMMY
    '71124', // GES_SEGUROS
    '95083', // ECOCEMENTOS DUMMY
    '82658', // ECOCEMENTOS PRO
    '6001', // TOPCLIMA
    '6005', // TOPCLIMA_ONLINE
    '114835', // INDUSTRIASROYALTERMICSL
];

const MainNavigationToolbar = memo(({ onExpand }) => {
    // --- Context & hooks
    const dispatch = useDispatch();

    // --- State
    const {
        activeEntityName,
        analyticsPro,
        brandingWebColor,
        defaultEnvironment,
        idImplementation,
        isDeletedWalkThroughData,
        isImpersonated,
        logoRectangular,
        logoSquare,
        messages,
        canAccessCampaigns,
        hasEmailCampaigns,
        campaignsRoute,
        permission = EMPTY_OBJECT,
        showEntityPlaceholder,
        withDashboard,
    } = useSelector(
        (state) => ({
            activeEntityName: state.entityList?.active,
            analyticsPro: state.config?.permission?.analyticsPro || false,
            brandingWebColor: state.config?.permission?.brandingWebColor || null,
            defaultEnvironment: state.config?.userData?.idEntorno,
            idImplementation: state.config?.userData?.idImplementacion,
            isDeletedWalkThroughData: state.config?.userData?.isDeletedWalkThroughData,
            isImpersonated: state.config?.userData?.isImpersonated,
            logoRectangular: state.config?.permission?.customerSidebarLogoRectangularUrlWeb || null,
            logoSquare: state.config?.permission?.customerSidebarLogoSquareUrlWeb || null,
            messages: state.messages?.unread?.all?.entities,
            canAccessCampaigns: canAccessCampaignsSection(state),
            hasEmailCampaigns: canAccessEmailCampaigns(state),
            campaignsRoute: getCampaignsDefaultRoute(state),
            permission: state.config?.permission,
            showEntityPlaceholder: state.config?.showEntityPlaceholder,
            subscriptionModeCode: state.config?.subscriptionModeCode,
            withDashboard: state.config.userData?.dashboardActive || false,
        }),
        shallowEqual,
    );
    const environmentsRaw = useSelector(
        (state) => state?.serverList?.tblSucursales?.data || EMPTY_ARRAY,
    );
    const literals = useSelector((state) => state.literal.literals);
    const selectedMultiEnvironmentIds = useSelector(getSelectedMultiEnvironmentIds, shallowEqual);
    const subscriptionModeCode = useSelector((state) => state.config?.subscriptionModeCode);
    const withMultiEnvironmentSection = useSelector(isMultiEnvironmentModeActive);
    const [currentHref, setCurrentHref] = useState(window.location.href);

    // --- Derived state
    const activitiesNotificationsBadge = useMemo(
        () => (messages ? getActivitiesNotificationsBadge(messages) : 0),
        [messages],
    );
    const environments = useMemo(
        () =>
            environmentsRaw.map((env) => ({
                label: env.label,
                value: env.value,
            })),
        [environmentsRaw],
    );
    const selectedEnvironmentIdsSet = useMemo(
        () => new Set(selectedMultiEnvironmentIds),
        [selectedMultiEnvironmentIds],
    );
    const environmentsSelected = useMemo(
        () => environments.filter((env) => selectedEnvironmentIdsSet.has(env.value)),
        [environments, selectedEnvironmentIdsSet],
    );
    const customLogo = useMemo(
        () =>
            logoSquare || logoRectangular
                ? {
                      square: logoSquare,
                      rectangular: logoRectangular,
                  }
                : EMPTY_LOGO,
        [logoRectangular, logoSquare],
    );
    const activeMultiEnvironmentEntity = useMemo(() => {
        const activeEntityConfig = activeEntityName ? Entities[activeEntityName] : null;
        return activeEntityConfig?.isMultiEnvironment ? activeEntityConfig : null;
    }, [activeEntityName]);
    const resolveLiteral = useCallback(
        (literal) => {
            if (literals && Object.prototype.hasOwnProperty.call(literals, literal)) {
                return literals?.[literal]?.split('\\n').join('\n') || literals[literal];
            }

            return literal;
        },
        [literals],
    );
    const menuLiterals = useMemo(
        () => ({
            accounts: resolveLiteral('title_accounts'),
            activities: resolveLiteral('title_activities'),
            agenda: resolveLiteral('title_agenda'),
            billboard: resolveLiteral('title_billboard_billboards'),
            campaigns: resolveLiteral('title_campaigns'),
            contacts: resolveLiteral('title_contacts'),
            customPage: resolveLiteral('title_custom_page'),
            dashboard: resolveLiteral('title_dashboard'),
            documents: resolveLiteral('title_documents'),
            kpis: resolveLiteral('title_admin_kpis'),
            metrics: resolveLiteral('label_metrics_and_reports'),
            opportunities: resolveLiteral('title_opportunities'),
            products: resolveLiteral('title_products'),
            salesorders: resolveLiteral('title_salesorders'),
        }),
        [resolveLiteral],
    );
    // --- Handlers
    const navigate = useCallback(
        (route) => () => {
            ensureRoute(route);
        },
        [],
    );

    const onClick = useCallback(
        (entityName, submodule) => () => {
            let obj = {
                event: entityName,
                functionality: 'menuClick',
            };

            if (submodule) obj.submodule = submodule;

            logEvent(obj);
        },
        [],
    );
    const handleGetUnreadMessages = useCallback(() => {
        dispatch(MessagesActions.getUnreadMessages());
    }, [dispatch]);
    const handleEnvironmentsChange = useCallback(
        (newEnvironments) => {
            const environmentIds = newEnvironments.map((env) => env.value);
            dispatch(
                ConfigActions.setSelectedMultiEnvironments(
                    environmentIds,
                    activeMultiEnvironmentEntity,
                ),
            );
        },
        [activeMultiEnvironmentEntity, dispatch],
    );
    const getPostComponent = useCallback(
        (props) => {
            if (!isDeletedWalkThroughData && subscriptionModeCode === 'free_trial') {
                return <MenuSampleData {...props} />;
            }

            if (!isImpersonated && permission.enableAnnounceKitOption) {
                return <AnnounceKitComponent {...props} />;
            }

            return null;
        },
        [isDeletedWalkThroughData, isImpersonated, permission, subscriptionModeCode],
    );
    const onMatchActive = useCallback((item) => {
        if (!item.link) return false;

        const cleanLink = item.link;

        // Avoiding double selection
        if (cleanLink?.includes('web3/index') && window.location.pathname.includes('web3/index')) {
            return true;
        } else if (window.location.pathname.includes('web3/index')) {
            return false;
        }

        return (
            window.location.href.includes(cleanLink) ||
            cleanLink === window.location.pathname ||
            window.location.href.includes(item.entityConversationsLink) ||
            item.entityConversationsLink === window.location.pathname ||
            (window.location.hash &&
                window.location.hash !== '#/' &&
                cleanLink?.includes(window.location.hash))
        );
    }, []);

    // --- Effects
    useEffect(() => {
        return hashHistory.listen(() => setCurrentHref(window.location.href));
    }, []);

    useEffect(() => {
        // Getting user unread messages
        // TO-DO: Refactor whenever there's a websockets service available
        if (!getEnableTimeLine()) return undefined;

        const unreadMessagesInterval = setInterval(handleGetUnreadMessages, INTERVAL_NEW_MESSAGES);

        return () => clearInterval(unreadMessagesInterval);
    }, [handleGetUnreadMessages]);

    // --- Derived for render
    const schema = useMemo(() => {
        const sections = [];
        const accountsMenu = {
                type: 'link',
                icon: 'accounts',
                name: menuLiterals.accounts,
                link: getRoute(COMPANIES.route),
                navigate: navigate(COMPANIES.route),
                onClick: onClick(COMPANIES.trueName),
                isMultiEnvironment: true,
            },
            contactsMenu = {
                type: 'link',
                icon: 'contacts',
                name: menuLiterals.contacts,
                link: getRoute(CONTACTS.route),
                navigate: navigate(CONTACTS.route),
                onClick: onClick(CONTACTS.trueName),
                isMultiEnvironment: true,
            },
            activitiesMenu = {
                type: 'link',
                icon: 'activities',
                name: menuLiterals.activities,
                link: getRoute(ACTIVITIES.route),
                navigate: navigate(ACTIVITIES.route),
                entityConversationsLink: getRoute(`${CONVERSATIONS.route}${ACTIVITIES.route}`),
                hasBadge: true,
                badgeContent: activitiesNotificationsBadge,
            },
            agendaMenu = {
                type: 'link',
                icon: 'calendar',
                name: menuLiterals.agenda,
                link: getRoute(AGENDA.route),
                navigate: navigate(AGENDA.route),
                onClick: onClick(AGENDA.trueName),
            },
            documentsMenu = {
                type: 'link',
                icon: 'documents',
                name: menuLiterals.documents,
                link: getRoute(DOCUMENTS.route),
                navigate: navigate(DOCUMENTS.route),
                onClick: onClick(DOCUMENTS.trueName),
            },
            opportunitiesMenu = {
                type: 'link',
                icon: 'opportunities',
                name: menuLiterals.opportunities,
                link: getRoute(OPPORTUNITIES.route),
                navigate: navigate(OPPORTUNITIES.route),
                onClick: onClick(OPPORTUNITIES.trueName),
                isMultiEnvironment: true,
            },
            productsMenu = {
                type: 'link',
                icon: 'products',
                name: menuLiterals.products,
                link: getRoute(PRODUCTS.route),
                navigate: navigate(PRODUCTS.route),
                onClick: onClick(PRODUCTS.trueName),
                isMultiEnvironment: true,
            },
            salesordersMenu = {
                type: 'link',
                icon: 'salesOrders',
                name: menuLiterals.salesorders,
                link: getRoute(SALESORDERS.route),
                navigate: navigate(SALESORDERS.route),
                onClick: onClick(SALESORDERS.trueName),
                isMultiEnvironment: true,
            },
            kpisMenu = {
                type: 'link',
                icon: 'goals',
                name: menuLiterals.kpis,
                link: getRoute(KPIS.route),
                navigate: navigate(KPIS.route),
                onClick: onClick(KPIS.trueName),
            },
            metricsMenu = {
                type: 'link',
                icon: 'analyticsPro',
                name: menuLiterals.metrics,
                link: getRoute(METRICS.route),
                navigate: navigate(METRICS.route),
                onClick: onClick(METRICS.trueName),
            },
            billboardMenu = {
                type: 'link',
                icon: 'news',
                name: menuLiterals.billboard,
                link: getRoute(BILLBOARD.route),
                navigate: navigate(BILLBOARD.route),
                onClick: onClick(BILLBOARD.trueName),
            },
            campaignsMenu = {
                type: 'link',
                icon: 'campaigns',
                name: menuLiterals.campaigns,
                link: getRoute(campaignsRoute),
                navigate: navigate(campaignsRoute),
                onClick: onClick(CAMPAIGNS.trueName),
            },
            customPageMenu = {
                type: 'link',
                icon: 'widgets',
                name: menuLiterals.customPage,
                link: getRoute('/custom-page'),
                navigate: navigate('/custom-page'),
            },
            dashboardMenu = {
                type: 'link',
                icon: 'dashboardWeb',
                name: menuLiterals.dashboard,
                link: getRoute('/dashboard'),
                navigate: navigate('/dashboard'),
            };

        if (withDashboard) {
            sections.push(dashboardMenu);
        }

        // Accounts
        if (permission['empresas']) {
            sections.push(accountsMenu);
        }

        // Contacts
        if (permission['contactos']) {
            sections.push(contactsMenu);
        }

        // Activities
        if (permission['gestiones']) {
            sections.push(activitiesMenu);
        }

        // Agenda
        if (permission['calendar']) {
            sections.push(agendaMenu);
        }

        // Opportunities
        if (permission['expedientes']) {
            sections.push(opportunitiesMenu);
        }

        // products
        if (permission['tarifa']) {
            sections.push(productsMenu);
        }

        // salesorders
        if (permission['salesOrders']) {
            sections.push(salesordersMenu);
        }

        // documents
        if (permission['documentManagement'] && permission['documentos_general']) {
            sections.push(documentsMenu);
        }

        if (
            (permission['informes'] && permission['reports_general']) ||
            permission['sfm'] ||
            analyticsPro
        ) {
            sections.push(metricsMenu);
        }

        // kpis
        if (permission['kpisPremium']) {
            sections.push(kpisMenu);
        }

        // billboard
        if (permission['billboard']) {
            sections.push(billboardMenu);
        }

        // campaigns
        if (canAccessCampaigns) {
            sections.push(campaignsMenu);
        }

        // custom page
        const customPageActive =
            idImplementation && PAGE_WIDGET_IMPLEMENTATIONS.indexOf(idImplementation) !== -1;
        if (permission['custom_page'] && customPageActive) {
            sections.push(customPageMenu);
        }

        // AddOns Placeholders
        if (
            (!permission['kpisPremium'] ||
                !permission['salesOrders'] ||
                !permission['tarifa'] ||
                !permission['billboard'] ||
                !permission['verCampaignsAdmin']) &&
            permission['viewAddonsConfiguration']
        ) {
            if (!permission['kpisPremium'] && showEntityPlaceholder?.[KPIS.entity])
                sections.push({
                    type: 'link',
                    icon: 'goals',
                    link: getRoute('/try-kpis'),
                    name: menuLiterals.kpis,
                    hasTrial: true,
                    onClick: onClick(KPIS.trueName, 'addons'),
                });
            if (!permission['salesOrders'] && showEntityPlaceholder?.[SALESORDERS.entity])
                sections.push({
                    type: 'link',
                    icon: 'salesOrders',
                    link: getRoute('/try-sales-orders'),
                    name: menuLiterals.salesorders,
                    hasTrial: true,
                    onClick: onClick(SALESORDERS.trueName, 'addons'),
                });
            if (!permission['tarifa'] && showEntityPlaceholder?.[PRODUCTS.entity])
                sections.push({
                    type: 'link',
                    icon: 'products',
                    link: getRoute('/try-products'),
                    name: menuLiterals.products,
                    hasTrial: true,
                    onClick: onClick(PRODUCTS.trueName, 'addons'),
                });
            if (!permission['billboard'] && showEntityPlaceholder?.[BILLBOARD.entity])
                sections.push({
                    type: 'link',
                    icon: 'news',
                    link: getRoute('/try-newsmanager'),
                    name: menuLiterals.billboard,
                    hasTrial: true,
                    onClick: onClick(BILLBOARD.trueName, 'addons'),
                });
            if (
                !permission['verCampaignsAdmin'] &&
                !hasEmailCampaigns &&
                showEntityPlaceholder?.[CAMPAIGNS.entity]
            )
                sections.push({
                    type: 'link',
                    icon: 'campaigns',
                    link: getRoute('/try-salescampaigns'),
                    name: menuLiterals.campaigns,
                    hasTrial: true,
                    onClick: onClick(CAMPAIGNS.trueName, 'addons'),
                });
        }

        return { appName: 'web', homeLink: '/', menuItems: sections };
    }, [
        activitiesNotificationsBadge,
        analyticsPro,
        campaignsRoute,
        canAccessCampaigns,
        hasEmailCampaigns,
        idImplementation,
        menuLiterals,
        navigate,
        onClick,
        permission,
        showEntityPlaceholder,
        withDashboard,
    ]);

    // --- Memoized props
    const sidebarProps = useMemo(
        () => ({
            currentHref,
            customColor: brandingWebColor,
            customLogo,
            defaultEnvironment,
            environments,
            environmentsSelected,
            getPostComponent,
            onEnvironmentsChange: handleEnvironmentsChange,
            onExpand,
            onMatchActive,
            schema,
            withMultiEnvironmentSection,
        }),
        [
            brandingWebColor,
            currentHref,
            customLogo,
            defaultEnvironment,
            environments,
            environmentsSelected,
            getPostComponent,
            handleEnvironmentsChange,
            onExpand,
            onMatchActive,
            schema,
            withMultiEnvironmentSection,
        ],
    );

    // --- Render
    return (
        <SidebarProvider>
            <Sidebar {...sidebarProps} />
        </SidebarProvider>
    );
});

MainNavigationToolbar.displayName = 'MainNavigationToolbar';

export default MainNavigationToolbar;
